import React from 'react'
import { Dropdown, MenuProps } from 'antd'
import checkIcon from 'assets/images/check-mark-16.svg'
import chevronDownIcon from 'assets/images/chevron-down-24.svg'
import taskIcon from 'assets/images/todo/property-tasks.svg'
import classNames from 'classnames'
import i18n from 'i18n'
import SVG from 'react-inlinesvg'
import { TodoFolder } from '../../modules/TodoFolder'
import styles from './style.module.scss'

interface FolderSelectorProps {
  selectedFolder: TodoFolder | null
  folders: TodoFolder[]
  onFolderChange: (folder: TodoFolder | null) => void
  disabled?: boolean
  settingType?: string | null
  externalClassName?: string | null
}

const FolderSelector: React.FC<FolderSelectorProps> = ({
  selectedFolder,
  folders,
  onFolderChange,
  disabled,
  settingType = null,
  externalClassName = null,
}) => {
  const getLabel = (folder: TodoFolder | null) => {
    return (
      <div className={styles.menuItem} style={{ gap: '8px' }}>
        <div className={styles.menuItemContent}>
          <div
            className={styles.colorDot}
            style={{
              backgroundColor: folder ? folder.calendarColor?._cdata || '#fd4c45' : 'transparent',
            }}
          />
          <div className={styles.folderInfo}>
            <div className={styles.folderName}>
              {folder ? folder.displayName : getNullFolderName()}
            </div>
            <div className={styles.folderOwner}>{folder?.owner || folder?.userInfo?.email}</div>
          </div>
        </div>
        {selectedFolder?.url === folder?.url ? (
          React.createElement(SVG as any, {
            src: checkIcon,
            alt: 'Selected',
            className: styles.checkIcon,
          })
        ) : (
          <div className={styles.checkIcon} />
        )}
      </div>
    )
  }

  const getFolderItems = (): MenuProps['items'] => {
    const folderItems =
      folders
        ?.filter((folder) => !folder.isReadOnly)
        .map((folder) => ({
          key: folder.url,
          label: getLabel(folder),
          onClick: () => onFolderChange(folder),
        })) || []

    if (settingType !== null) {
      folderItems.unshift({
        key: 'lastFolderItem',
        label: getLabel(null),
        onClick: () => onFolderChange(null),
      })
    }

    return folderItems
  }

  const getNullFolderName = (): string => {
    if (settingType === null) {
      return 'Tasks'
    }

    return settingType === 'todo'
      ? i18n.t('settings.calendarTabs.lastTaskAdded')
      : i18n.t('settings.calendarTabs.lastEventAdded')
  }

  /* Settings 에서 사용되는 컴포넌트 이므로 CSS 수정이 필요하면 Settings/Todo/style.module.scss 에도 수정이 필요할 수 있음. */
  return (
    <div className={classNames(styles.folderSelector, 'folderSelector')}>
      <div className={classNames(styles.titleIconWrapper, 'titleIconWrapper')}>
        {React.createElement(SVG as any, {
          src: taskIcon,
          className: styles.titleIcon,
          alt: 'tasks',
          style: { color: selectedFolder?.calendarColor?._cdata || '#fdff45' },
        })}
      </div>
      <Dropdown
        menu={{ items: getFolderItems() }}
        trigger={['click']}
        placement="bottomRight"
        getPopupContainer={() => document.body}
        disabled={disabled}
        overlayClassName={classNames(styles.dropdown, externalClassName || '')}
      >
        <button
          className={classNames(styles.folderButton, 'folderButton', disabled ? styles.dimmed : '')}
          disabled={disabled}
        >
          <div className={styles.folderDetails}>
            <div className={styles.folderInfo}>
              <div className={classNames(styles.folderName, 'folderName')}>
                {selectedFolder?.displayName || getNullFolderName()}
              </div>
              <div className={styles.folderOwner}>
                <div className={classNames(styles.folderOwnerEmail, 'folderOwnerEmail')}>
                  {selectedFolder?.owner || selectedFolder?.userInfo?.email}
                </div>
              </div>
            </div>
          </div>
          <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />
        </button>
      </Dropdown>
    </div>
  )
}

export default FolderSelector
